<template>
  <v-container class="my-anamneses-page" style="max-width: 1100px">
    <!-- Cabeçalho da página -->
    <div class="d-flex align-center flex-wrap mb-4 mt-2">
      <div>
        <div class="page-section-header mb-1">
          <v-icon small color="primary" class="mr-2">
            mdi-clipboard-text-multiple
          </v-icon>
          <span class="page-section-title">Configuração</span>
        </div>
        <h1 class="page-title">Minhas Anamneses</h1>
      </div>
      <v-spacer></v-spacer>
      <v-chip
        small
        color="primary"
        text-color="white"
        class="mr-2"
        data-tour="an-active-count"
      >
        <v-icon left small>mdi-check-circle</v-icon>
        {{ activeCount }} ativa(s) no perfil
      </v-chip>
      <v-btn small text :loading="loading" @click="loadTemplates">
        <v-icon left small>mdi-refresh</v-icon>Atualizar
      </v-btn>
    </div>

    <!-- Aviso médico novo -->
    <v-alert
      v-if="onlyDefault"
      type="info"
      text
      dense
      border="left"
      class="mb-4"
    >
      Você ainda não criou anamneses personalizadas. A anamnese
      <strong>Padrão</strong> está sempre disponível — você pode
      <strong>criar a partir dela</strong> ou começar do zero.
    </v-alert>

    <div class="d-flex align-center mb-3">
      <h2 class="text-h6 font-weight-bold">Anamneses disponíveis</h2>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <v-btn
          color="primary"
          outlined
          class="mr-2"
          data-tour="an-from-default"
          @click="cloneFromDefault"
        >
          <v-icon left>mdi-content-copy</v-icon>Criar a partir da Padrão
        </v-btn>
        <v-btn
          color="primary"
          depressed
          data-tour="an-new"
          @click="newBlank"
        >
          <v-icon left>mdi-plus</v-icon>Nova anamnese
        </v-btn>
      </div>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-3"
    ></v-progress-linear>

    <!-- Lista de anamneses -->
    <v-row>
      <v-col
        v-for="(t, ti) in templates"
        :key="t.id"
        cols="12"
        md="6"
        :data-tour="ti === 0 ? 'an-card' : null"
      >
        <v-card outlined :class="t.active ? 'elevation-2' : ''">
          <v-card-title class="d-flex align-center">
            <span class="text-subtitle-1 font-weight-bold">{{ t.name }}</span>
            <v-chip
              v-if="t.isDefault"
              x-small
              color="blue-grey"
              dark
              class="ml-2"
            >
              <v-icon left x-small>mdi-lock</v-icon>Padrão
            </v-chip>
            <v-chip
              v-else
              x-small
              color="deep-purple lighten-1"
              dark
              class="ml-2"
              >Personalizada</v-chip
            >
            <v-spacer></v-spacer>
            <v-chip
              x-small
              :color="t.active ? 'success' : 'grey lighten-1'"
              :dark="t.active"
            >
              {{ t.active ? "Ativa" : "Inativa" }}
            </v-chip>
          </v-card-title>
          <v-card-subtitle
            >{{ t.questions.length }} pergunta(s)</v-card-subtitle
          >

          <v-card-text class="pt-0">
            <div
              v-for="(q, i) in t.questions.slice(0, 3)"
              :key="i"
              class="prompt-preview text-truncate"
            >
              {{ i + 1 }}. {{ q.prompt }}
            </div>
            <div v-if="t.questions.length > 3" class="prompt-preview">
              + {{ t.questions.length - 3 }} outra(s)…
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-switch
              :input-value="t.active"
              @change="toggleActive(t)"
              dense
              hide-details
              class="mt-0 ml-2"
              :data-tour="ti === 0 ? 'an-toggle' : null"
              :label="t.active ? 'Ativa no perfil' : 'Ativar no perfil'"
            ></v-switch>
            <v-spacer></v-spacer>
            <div
              class="d-flex align-center"
              :data-tour="ti === 0 ? 'an-card-actions' : null"
            >
              <v-btn icon small title="Visualizar" @click="openView(t)">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn icon small title="Duplicar" @click="clone(t)">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                title="Editar"
                :disabled="t.isDefault"
                @click="openEdit(t)"
              >
                <v-icon small>{{
                  t.isDefault ? "mdi-lock" : "mdi-pencil"
                }}</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                title="Excluir"
                :disabled="t.isDefault"
                @click="remove(t)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- ============ EDITOR ============ -->
    <v-dialog
      v-model="editorOpen"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card v-if="draft">
        <v-app-bar color="primary" dark flat>
          <v-btn class="close-btn" icon @click="cancelEdit"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>{{
            draft.id ? "Editar anamnese" : "Nova anamnese"
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Ver tour do editor"
            @click="startEditorTour"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn text @click="cancelEdit">Cancelar</v-btn>
          <v-btn
            color="white"
            depressed
            class="primary--text"
            :loading="saving"
            data-tour="an-ed-save"
            @click="saveDraft"
          >
            <v-icon left>mdi-content-save</v-icon>Salvar
          </v-btn>
        </v-app-bar>

        <!-- Corpo areia com o formulário em seções-cartão, como os demais diálogos. -->
        <v-sheet class="editor-body" min-height="100%">
          <v-container style="max-width: 900px">
            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-clipboard-text-outline</v-icon>
                  <span>Identificação</span>
                </div>
              </div>
              <v-text-field
                v-model="draft.name"
                label="Nome da anamnese"
                outlined
                dense
                hide-details="auto"
                background-color="white"
                placeholder="Ex.: Anamnese cardiológica inicial"
                data-tour="an-ed-name"
              ></v-text-field>
            </section>

            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-format-list-numbered</v-icon>
                  <span>Perguntas</span>
                </div>
                <span class="text-caption grey--text drag-hint"
                  >use as setas para reordenar</span
                >
              </div>

              <v-expansion-panels v-model="openPanel" focusable>
                <v-expansion-panel
                  v-for="(q, qi) in draft.questions"
                  :key="q.uid"
                  :data-tour="qi === 0 ? 'an-ed-question' : null"
                >
                  <v-expansion-panel-header>
                    <div class="d-flex align-center" style="width: 100%">
                      <v-icon
                        small
                        class="mr-2"
                        :color="q.source === 'livre' ? 'deep-purple' : 'primary'"
                      >
                        {{
                          q.source === "livre"
                            ? "mdi-pencil-outline"
                            : "mdi-database"
                        }}
                      </v-icon>
                      <span class="text-truncate" style="max-width: 60%">
                        {{ qi + 1 }}. {{ q.prompt || "(sem texto)" }}
                      </span>
                      <v-chip x-small class="ml-2" outlined>{{
                        answerTypeLabel(q.answerType)
                      }}</v-chip>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        x-small
                        @click.stop="moveQ(qi, -1)"
                        :disabled="qi === 0"
                        ><v-icon x-small>mdi-arrow-up</v-icon></v-btn
                      >
                      <v-btn
                        icon
                        x-small
                        @click.stop="moveQ(qi, 1)"
                        :disabled="qi === draft.questions.length - 1"
                        ><v-icon x-small>mdi-arrow-down</v-icon></v-btn
                      >
                      <v-btn icon x-small @click.stop="removeQ(qi)"
                        ><v-icon x-small>mdi-delete</v-icon></v-btn
                      >
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-textarea
                      v-model="q.prompt"
                      label="Texto da pergunta"
                      rows="2"
                      auto-grow
                      outlined
                      dense
                      background-color="white"
                      class="mt-2"
                    ></v-textarea>

                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="q.answerType"
                          :items="answerTypes"
                          label="Tipo de resposta"
                          outlined
                          dense
                          hide-details="auto"
                          background-color="white"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="q.source"
                          :items="sources"
                          label="Origem"
                          outlined
                          dense
                          hide-details="auto"
                          background-color="white"
                          @change="onSourceChange(q)"
                        ></v-select>
                      </v-col>
                    </v-row>

                    <!-- Catálogo: escolhe domínio, pré-preenche opções com SNOMED -->
                    <v-select
                      v-if="q.source === 'catalogo'"
                      v-model="q.domain"
                      :items="domainNames"
                      label="Domínio do catálogo"
                      outlined
                      dense
                      hide-details="auto"
                      background-color="white"
                      hint="As opções e os códigos SNOMED vêm do catálogo clínico"
                      persistent-hint
                      class="mt-2"
                      @change="applyDomain(q)"
                    ></v-select>

                    <!-- Opções (para tipos de escolha) -->
                    <div v-if="hasOptions(q.answerType)" class="mt-3">
                      <div class="text-caption font-weight-bold mb-1">
                        Opções de resposta
                      </div>
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th style="width: 45%">Rótulo</th>
                            <th>TUSS</th>
                            <th>SNOMED</th>
                            <th style="width: 120px" class="text-center">
                              Correlação
                            </th>
                            <th style="width: 40px"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(op, oi) in q.options" :key="oi">
                            <td>
                              <v-text-field
                                v-model="op.label"
                                dense
                                hide-details
                                flat
                                solo
                                background-color="transparent"
                              ></v-text-field>
                            </td>
                            <td>
                              <v-text-field
                                v-model="op.tuss"
                                dense
                                hide-details
                                flat
                                solo
                                placeholder="—"
                                class="code-chip"
                              ></v-text-field>
                            </td>
                            <td>
                              <v-text-field
                                v-model="op.snomed"
                                dense
                                hide-details
                                flat
                                solo
                                placeholder="—"
                                class="code-chip"
                              ></v-text-field>
                            </td>
                            <td class="text-center">
                              <v-btn
                                x-small
                                text
                                color="deep-purple"
                                :loading="op._loading"
                                @click="suggestCode(op)"
                              >
                                <v-icon left x-small>mdi-robot-outline</v-icon>IA
                              </v-btn>
                            </td>
                            <td>
                              <v-btn icon x-small @click="q.options.splice(oi, 1)"
                                ><v-icon x-small>mdi-close</v-icon></v-btn
                              >
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                      <v-btn
                        text
                        x-small
                        color="primary"
                        class="mt-1"
                        @click="q.options.push({ label: '', tuss: '', snomed: '' })"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>Adicionar opção
                      </v-btn>
                    </div>

                    <!-- Pergunta livre de texto: correlação da própria pergunta -->
                    <div v-else-if="q.source === 'livre'" class="mt-3">
                      <v-row dense align="center">
                        <v-col cols="6" sm="4"
                          ><v-text-field
                            v-model="q.tuss"
                            label="TUSS"
                            dense
                            outlined
                            hide-details
                            background-color="white"
                            class="code-chip"
                          ></v-text-field
                        ></v-col>
                        <v-col cols="6" sm="4"
                          ><v-text-field
                            v-model="q.snomed"
                            label="SNOMED"
                            dense
                            outlined
                            hide-details
                            background-color="white"
                            class="code-chip"
                          ></v-text-field
                        ></v-col>
                        <v-col cols="12" sm="4">
                          <v-btn
                            small
                            text
                            color="deep-purple"
                            :loading="q._loading"
                            @click="suggestCode(q, q.prompt)"
                          >
                            <v-icon left small>mdi-robot-outline</v-icon>Sugerir
                            código (IA)
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <v-switch
                      v-model="q.acceptAudio"
                      dense
                      hide-details
                      label="Aceitar resposta em áudio"
                      class="mt-3"
                    ></v-switch>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-btn
                color="primary"
                outlined
                block
                class="mt-4"
                data-tour="an-ed-add"
                @click="addQuestion"
              >
                <v-icon left>mdi-plus</v-icon>Adicionar pergunta
              </v-btn>
            </section>
          </v-container>
        </v-sheet>
      </v-card>
    </v-dialog>

    <!-- ============ VISUALIZAR ============ -->
    <v-dialog v-model="viewOpen" max-width="640" scrollable>
      <v-card v-if="viewing">
        <v-card-title>
          {{ viewing.name }}
          <v-chip
            v-if="viewing.isDefault"
            x-small
            color="blue-grey"
            dark
            class="ml-2"
            ><v-icon left x-small>mdi-lock</v-icon>Padrão</v-chip
          >
        </v-card-title>
        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-format-list-numbered</v-icon>
                <span>Perguntas</span>
              </div>
              <span class="ds-form-section__aside">{{
                (viewing.questions || []).length
              }}</span>
            </div>
            <div v-for="(q, i) in viewing.questions" :key="i" class="mb-3">
              <div class="font-weight-medium">{{ i + 1 }}. {{ q.prompt }}</div>
              <div class="text-caption grey--text">
                {{ answerTypeLabel(q.answerType) }} ·
                {{
                  q.source === "livre" ? "pergunta livre" : "catálogo: " + q.domain
                }}
                <span v-if="q.acceptAudio"> · aceita áudio</span>
              </div>
              <div v-if="hasOptions(q.answerType)" class="mt-1">
                <v-chip
                  v-for="(op, oi) in q.options"
                  :key="oi"
                  x-small
                  outlined
                  class="mr-1 mb-1"
                >
                  {{ op.label
                  }}<span v-if="op.snomed" class="ml-1 code-chip grey--text"
                    >· {{ op.snomed }}</span
                  >
                </v-chip>
              </div>
            </div>
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" @click="viewOpen = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :timeout="2500" :color="snack.color" dark>{{
      snack.text
    }}</v-snackbar>
  </v-container>
</template>

<script>
import tourMixin from "@/features/tour/tourMixin";
import tour from "@/features/tour/tourService";
import {
  listAnamneseTemplates,
  createAnamneseTemplate,
  updateAnamneseTemplate,
  deleteAnamneseTemplate,
  setAnamneseTemplateActive,
} from "@/services/anamneseTemplateService";

// ---- Catálogo clínico mockado (subset real de dictionaries_new.py, com SNOMED) ----
// Usado só na UX do editor (picker de domínio + sugestão IA). A definição real e a
// persistência vivem na comunicação (tabelas questionnaire_*), via Laravel.
const CATALOG = {
  Objetivos: [
    { label: "Prevenção e check-up geral", snomed: "275926002" },
    { label: "Tratar um problema de saúde específico", snomed: "445142003" },
    { label: "Cuidar do bem-estar emocional ou mental", snomed: "410223002" },
    { label: "Receber orientação sobre alimentação e nutrição", snomed: "11816003" },
    { label: "Melhorar o desempenho esportivo", snomed: "325181000000106" },
    { label: "Outro", snomed: "" },
  ],
  Doenças: [
    { label: "Doenças cardiovasculares", snomed: "56265001" },
    { label: "Doenças respiratórias", snomed: "50043002" },
    { label: "Problemas gastrointestinais", snomed: "24813008" },
    { label: "Doenças endócrinas/metabólicas", snomed: "75934005" },
    { label: "Saúde mental", snomed: "74732009" },
    { label: "Outra(s)", snomed: "" },
  ],
  Sintomas: [
    { label: "Distensão abdominal", snomed: "116289008" },
    { label: "Constipação / prisão de ventre", snomed: "14760008" },
    { label: "Diarréia", snomed: "62315008" },
    { label: "Queda de cabelo", snomed: "" },
    { label: "Insônia", snomed: "193462001" },
    { label: "Outro(s)", snomed: "" },
  ],
  "Qualidade do sono": [
    { label: "Boa - Acordo bem recuperado(a)", snomed: "11741000202105" },
    { label: "Irregular - Acordo cansado(a) com frequência", snomed: "12262002" },
    { label: "Ruim - sinto que não descanso o suficiente", snomed: "301345002" },
    { label: "Tenho diagnóstico de insônia", snomed: "193462001" },
    { label: "Tenho diagnóstico de apnéia", snomed: "73430006" },
  ],
};

// Palavras-chave → código, pra simular a sugestão da IA em perguntas/opções livres
const AI_HINTS = [
  { kw: ["fuma", "cigarro", "tabag"], snomed: "77176002", tuss: "" },
  { kw: ["álcool", "alcool", "bebe", "bebida"], snomed: "228273003", tuss: "" },
  { kw: ["água", "agua", "hidrat"], snomed: "11713004", tuss: "" },
  { kw: ["dor de cabeça", "cefale", "enxaqueca"], snomed: "25064002", tuss: "" },
  { kw: ["pressão", "pressao", "hipertens"], snomed: "38341003", tuss: "" },
  { kw: ["diabet", "glicemia", "açúcar"], snomed: "73211009", tuss: "" },
  { kw: ["sono", "dorme", "insôn", "inson"], snomed: "193462001", tuss: "" },
  { kw: ["peso", "emagre"], snomed: "27113001", tuss: "" },
  { kw: ["exercí", "exerci", "treino", "atividade física"], snomed: "256235009", tuss: "" },
];

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

// Garante que cada pergunta tenha um `uid` estável para o v-for do editor
// (o backend não persiste uid — é só chave de UI).
function ensureUids(template) {
  (template.questions || []).forEach((q) => {
    if (!q.uid) q.uid = uid();
  });
  return template;
}

export default {
  name: "MyAnamnesesPage",

  mixins: [tourMixin],

  data() {
    return {
      templates: [],
      loading: false,
      saving: false,
      editorOpen: false,
      draft: null,
      openPanel: 0,
      viewOpen: false,
      viewing: null,
      snack: { show: false, text: "", color: "success" },
      answerTypes: [
        { text: "Múltipla escolha", value: "multipla" },
        { text: "Escolha única", value: "unica" },
        { text: "Sim / Não", value: "sim_nao" },
        { text: "Texto livre", value: "texto" },
        { text: "Número", value: "numero" },
      ],
      sources: [
        { text: "Pergunta livre", value: "livre" },
        { text: "Domínio do catálogo (SNOMED)", value: "catalogo" },
      ],
    };
  },

  mounted() {
    this.loadTemplates();
  },

  watch: {
    // Tour do editor: auto-inicia (1ª vez) quando o editor abre.
    editorOpen(val) {
      if (val) this.$nextTick(() => this.tryEditorTour(true));
    },
  },

  computed: {
    tourId() {
      return "anamneses";
    },
    domainNames() {
      return Object.keys(CATALOG);
    },
    activeCount() {
      return this.templates.filter((t) => t.active).length;
    },
    onlyDefault() {
      return this.templates.length === 1 && this.templates[0].isDefault;
    },
  },

  methods: {
    tourReady() {
      return !this.loading;
    },
    tourSteps() {
      return [
        {
          title: "Minhas Anamneses 📋",
          description:
            "Aqui você cria e gerencia seus modelos de anamnese — os questionários que usa nos atendimentos. Existe sempre uma anamnese Padrão, e você pode criar quantas personalizadas quiser. Reabra este tour pelo “?” quando precisar.",
        },
        {
          anchor: "an-active-count",
          title: "Anamneses ativas no perfil",
          description:
            "Este selo mostra quantos modelos estão “ativos”. Só as anamneses ativas ficam disponíveis para responder no atendimento do paciente — pense nele como quais questionários você tem em uso.",
        },
        {
          anchor: "an-from-default",
          title: "Criar a partir da Padrão",
          description:
            "Copia a anamnese Padrão para uma nova personalizada, já preenchida — ideal para partir de algo pronto e só ajustar as perguntas.",
        },
        {
          anchor: "an-new",
          title: "Nova anamnese (do zero)",
          description:
            "Cria uma anamnese personalizada em branco, para você montar as perguntas do jeito que quiser.",
        },
        {
          anchor: "an-card",
          title: "Cada modelo é um card",
          description:
            "No card você vê o nome, se é Padrão (bloqueada) ou Personalizada, o status (Ativa/Inativa), quantas perguntas tem e uma prévia das primeiras.",
        },
        {
          anchor: "an-toggle",
          title: "Ativar no perfil",
          description:
            "Use este interruptor para ligar/desligar o modelo no atendimento. Ativar torna a anamnese disponível para ser respondida; desativar a guarda sem apagá-la.",
        },
        {
          anchor: "an-card-actions",
          title: "Ações do modelo",
          description:
            "Da esquerda para a direita: Visualizar (ver as perguntas), Duplicar, Editar e Excluir. A anamnese Padrão não pode ser editada nem excluída (aparece com cadeado) — para adaptá-la, use “Criar a partir da Padrão”.",
        },
      ];
    },

    // ── Tour do EDITOR (dialog fullscreen) — via tourService direto ──
    editorSteps() {
      return [
        {
          anchor: "an-ed-name",
          title: "Nome da anamnese",
          description:
            "Dê um nome claro ao modelo (ex.: “Anamnese cardiológica inicial”). É por ele que você reconhece a anamnese na lista e no atendimento.",
        },
        {
          anchor: "an-ed-question",
          title: "As perguntas",
          description:
            "Cada painel é uma pergunta. Abra para editar o texto, o tipo de resposta (texto, escolha, número…) e a origem — “livre” (você escreve) ou “catálogo” (puxa opções e códigos SNOMED/TUSS do catálogo clínico). Use as setas para reordenar e a lixeira para remover.",
        },
        {
          anchor: "an-ed-add",
          title: "Adicionar pergunta",
          description:
            "Inclui uma nova pergunta ao final da anamnese. Monte quantas quiser, na ordem que fizer sentido para o seu atendimento.",
        },
        {
          anchor: "an-ed-save",
          title: "Salvar",
          description:
            "Grava a anamnese. Depois de salva, ela aparece na lista e pode ser ativada no perfil para uso nos atendimentos.",
        },
      ];
    },

    startEditorTour() {
      this.tryEditorTour(false);
    },

    // Espera o editor renderizar (âncoras existirem) e então inicia o tour.
    tryEditorTour(auto, tries = 0) {
      const ready = document.querySelector('[data-tour="an-ed-name"]');
      if (ready) {
        if (auto) tour.maybeAutoStart("anamnese-editor", this.editorSteps());
        else tour.start("anamnese-editor", this.editorSteps());
        return;
      }
      if (tries < 20) {
        setTimeout(() => this.tryEditorTour(auto, tries + 1), 200);
      }
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    answerTypeLabel(v) {
      const m = this.answerTypes.find((a) => a.value === v);
      return m ? m.text : v;
    },
    hasOptions(v) {
      return v === "multipla" || v === "unica";
    },

    async loadTemplates() {
      this.loading = true;
      try {
        const list = await listAnamneseTemplates();
        this.templates = list.map((t) => ensureUids(t));
      } catch (e) {
        this.notify(this.errMsg(e, "Falha ao carregar anamneses"), "error");
      } finally {
        this.loading = false;
      }
    },

    async toggleActive(t) {
      const next = !t.active;
      t.active = next; // otimista
      try {
        await setAnamneseTemplateActive(t.id, next);
        this.notify(
          next ? `"${t.name}" ativada no perfil` : `"${t.name}" desativada`,
        );
      } catch (e) {
        t.active = !next; // reverte
        this.notify(this.errMsg(e, "Falha ao alterar o status"), "error");
      }
    },

    openView(t) {
      this.viewing = t;
      this.viewOpen = true;
    },

    newBlank() {
      this.draft = {
        id: null,
        name: "",
        isDefault: false,
        active: true,
        questions: [this.blankQuestion()],
      };
      this.openPanel = 0;
      this.editorOpen = true;
    },
    cloneFromDefault() {
      this.clone(this.templates.find((t) => t.isDefault));
    },
    clone(t) {
      const copy = JSON.parse(JSON.stringify(t));
      copy.id = null;
      copy.isDefault = false;
      copy.active = true;
      copy.name = t.isDefault
        ? "Minha anamnese (cópia da Padrão)"
        : t.name + " (cópia)";
      copy.questions.forEach((q) => (q.uid = uid()));
      this.draft = copy;
      this.openPanel = 0;
      this.editorOpen = true;
      this.notify("Cópia criada — edite e salve");
    },
    openEdit(t) {
      if (t.isDefault) return;
      this.draft = JSON.parse(JSON.stringify(t));
      this.draft.questions.forEach((q) => {
        if (!q.uid) q.uid = uid();
      });
      this.openPanel = 0;
      this.editorOpen = true;
    },
    blankQuestion() {
      return {
        uid: uid(),
        prompt: "",
        source: "livre",
        domain: null,
        answerType: "texto",
        acceptAudio: false,
        tuss: "",
        snomed: "",
        options: [],
      };
    },
    addQuestion() {
      this.draft.questions.push(this.blankQuestion());
      this.openPanel = this.draft.questions.length - 1;
    },
    removeQ(i) {
      this.draft.questions.splice(i, 1);
    },
    moveQ(i, dir) {
      const j = i + dir;
      const a = this.draft.questions;
      [a[i], a[j]] = [a[j], a[i]];
      this.draft.questions = [...a];
    },
    onSourceChange(q) {
      if (q.source === "catalogo") {
        if (!this.hasOptions(q.answerType)) q.answerType = "multipla";
      } else {
        q.domain = null;
      }
    },
    applyDomain(q) {
      if (!q.domain) return;
      q.options = CATALOG[q.domain].map((o) => ({
        label: o.label,
        tuss: "",
        snomed: o.snomed,
      }));
      this.notify(`Opções de "${q.domain}" carregadas do catálogo`);
    },
    suggestCode(target, text) {
      const base = (text || target.label || "").toLowerCase();
      this.$set(target, "_loading", true);
      window.setTimeout(() => {
        const hit = AI_HINTS.find((h) => h.kw.some((k) => base.includes(k)));
        if (hit) {
          target.snomed = hit.snomed;
          if (hit.tuss) target.tuss = hit.tuss;
          this.notify("IA sugeriu SNOMED " + hit.snomed, "deep-purple");
        } else {
          const fake = String(Math.floor(100000 + Math.random() * 900000000));
          target.snomed = fake;
          this.notify("IA sugeriu um código (revise): " + fake, "deep-purple");
        }
        this.$set(target, "_loading", false);
      }, 700);
    },
    async saveDraft() {
      if (!this.draft.name.trim()) {
        this.notify("Dê um nome à anamnese", "error");
        return;
      }
      const editId = this.draft.id;
      const payload = this.buildPayload();
      this.saving = true;
      try {
        if (editId) {
          const updated = await updateAnamneseTemplate(editId, payload);
          const idx = this.templates.findIndex((t) => t.id === editId);
          if (idx !== -1) this.$set(this.templates, idx, ensureUids(updated));
          this.notify("Anamnese atualizada");
        } else {
          const created = await createAnamneseTemplate(payload);
          this.templates.push(ensureUids(created));
          this.notify("Anamnese criada");
        }
        this.editorOpen = false;
        this.draft = null;
      } catch (e) {
        this.notify(this.errMsg(e, "Falha ao salvar a anamnese"), "error");
      } finally {
        this.saving = false;
      }
    },
    // Monta o "editor shape" que o Laravel espera ({name, questions:[...]}),
    // sem campos só de UI (uid/_loading).
    buildPayload() {
      const d = JSON.parse(JSON.stringify(this.draft));
      const questions = (d.questions || []).map((q) => {
        const { uid: _uid, _loading, ...rest } = q; // eslint-disable-line no-unused-vars
        (rest.options || []).forEach((o) => delete o._loading);
        return rest;
      });
      return { name: d.name.trim(), questions };
    },
    cancelEdit() {
      this.editorOpen = false;
      this.draft = null;
    },
    async remove(t) {
      if (t.isDefault) return;

      // salvia-776: apagava o modelo de anamnese SEM perguntar nada. Nao e
      // descarte de interface -- e um modelo que o profissional montou, e que
      // outras anamneses podem estar usando.
      const ok = await this.$confirm({
        type: "delete",
        message: `Excluir a anamnese <strong>${t.name}</strong>?`,
        hint: "As anamneses já respondidas com este modelo não são afetadas.",
      });
      if (!ok) return;

      try {
        await deleteAnamneseTemplate(t.id);
        this.templates = this.templates.filter((x) => x.id !== t.id);
        this.notify("Anamnese excluída", "error");
      } catch (e) {
        this.notify(this.errMsg(e, "Falha ao excluir a anamnese"), "error");
      }
    },
    errMsg(e, fallback) {
      return (
        (e && e.response && e.response.data && e.response.data.message) ||
        fallback
      );
    },
  },
};
</script>

<style scoped>
.my-anamneses-page {
  min-height: 100vh;
}

.page-section-header {
  display: flex;
  align-items: center;
}

.page-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary, #1976d2);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.page-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--ds-text-strong, #263238);
  letter-spacing: -0.4px;
  margin: 0;
}

.q-card {
  border-left: 4px solid #1976d2;
}
.q-card.q-livre {
  border-left-color: #7e57c2;
}
.drag-hint {
  cursor: default;
}
/* Fundo areia do editor em tela cheia: as seções-cartão brancas se destacam. */
.editor-body.v-sheet {
  background-color: var(--ds-blue-bg-soft) !important;
}
.prompt-preview {
  font-size: 13px;
  color: #607d8b;
}
.code-chip {
  font-family: monospace;
  font-size: 11px;
}
</style>
